/**
 * NotificationItem Component
 *
 * One row of the header dropdown: the notification's avatar, the API's title
 * and message, when it arrived, and its action — Accept / Decline for a
 * question, otherwise its one button beside a dismiss.
 */

import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { NotificationUtils } from '@personal-trainer-app/shared';
import { formatDisplayDate } from '@personal-trainer-app/shared/src/utils/date';

import type { NotificationItemProps } from './NotificationItem.types';

import {
  NotificationAvatar,
  getNotificationPresentation,
} from '@/components/notifications';
import { Button, IconButton } from '@/components/ui';

export function NotificationItem({
  notification,
  onRespond,
  onAction,
  onDismiss,
  isBusy = false,
  className = '',
}: NotificationItemProps) {
  const { title, message } = NotificationUtils.getEnvelope(notification);
  const { respond, primaryAction } = getNotificationPresentation(notification);
  const isUnread = notification.read_at === null;
  const timestamp = formatDisplayDate(notification.created_at, 'relativeShort');

  const handleDismiss = () => {
    onDismiss?.(notification.id);
  };

  return (
    <div
      className={`
        relative flex items-start gap-3 p-4
        transition-colors duration-fast
        ${isUnread ? 'bg-sakura-wash' : 'bg-transparent'}
        hover:bg-lift-3
        focus:outline-none focus-visible:ring-state-focus-inset
        ${className}
      `.trim()}
      data-menu-item
      tabIndex={0}
    >
      {/* Unread indicator */}
      {isUnread && (
        <div className="absolute left-1.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-primary-400" />
      )}

      <NotificationAvatar notification={notification} />

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Title — written by the API */}
        <p
          className={`
            text-body-md font-medium leading-snug
            ${isUnread ? 'text-neutral-50' : 'text-neutral-300'}
          `}
        >
          {title}
        </p>

        {/* Message */}
        <p className="text-body-md text-neutral-400 mt-0.5 line-clamp-2">
          {message}
        </p>

        {/* Timestamp */}
        <p className="text-body-sm text-neutral-500 mt-1.5">{timestamp}</p>
      </div>

      {/* Actions */}
      <div className="flex-shrink-0 flex items-center gap-2">
        {respond ? (
          respond.options.map((option) => (
            // Accept is the primary action, so sakura (not semantic green);
            // decline is a neutral dismissal, not a destructive/error action.
            <Button
              key={option.intent}
              variant={option.intent === 'accept' ? 'outline-primary' : 'ghost'}
              size="xs"
              disabled={isBusy}
              onClick={() => onRespond?.(notification, option)}
              aria-label={option.label}
            >
              <span className="sm:hidden">
                {option.intent === 'accept' ? (
                  <CheckIcon className="h-4 w-4" />
                ) : (
                  <XMarkIcon className="h-4 w-4" />
                )}
              </span>
              <span className="hidden sm:inline">{option.label}</span>
            </Button>
          ))
        ) : (
          <>
            {primaryAction && (
              <Button
                variant="ghost"
                size="xs"
                className="text-primary-300 hover:text-primary-300"
                loading={isBusy}
                onClick={() => onAction?.(notification)}
              >
                {primaryAction.label}
              </Button>
            )}
            <IconButton
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              aria-label="Dismiss"
              icon={<XMarkIcon className="h-4 w-4" />}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default NotificationItem;
